import { Dispatch, SetStateAction } from "react";
import { toNumber } from "lodash";
import { SelectListObject } from "@tvg/design-system/src/components/input/types";
import { Probable } from "@tvg/ts-types/Race";
import { WagerType, WagerTypeCodesEnum } from "@tvg/ts-types/Wager";
import formatCurrency, { formatPurse } from "@tvg/formatter/currency";
import { TableColumns } from "./types";
import { gtmOnSelectProbablesOption } from "../../../utils/gtm/willPaysNProbables";
import { DynamicSelectorListItem } from "../DynamicSelectorPro";

/**
 * Formats a given amount into a currency
 * @param amount string representation of an amount
 * @param precision
 * @returns formatted currency
 */
export const getFormattedAmount = (
  payout: number | string | undefined,
  precision: number,
  truncated: boolean = false
) => {
  if (payout === undefined) return formatCurrency(0, "USD", precision);

  // Solution to match MEP
  const amount = Math.floor(toNumber(payout));

  if (truncated) return formatPurse(amount, "USD", precision);

  return formatCurrency(amount, "USD", precision);
};

export const getWagersSelectionListPro = (
  probables: Probable[] | undefined
): DynamicSelectorListItem[] => {
  if (!probables) return [];

  let firstProbable = probables?.find((item) => item.wagerType.code === "EX");
  if (!firstProbable) {
    firstProbable = probables[0];
  }
  const leftProbables = probables?.filter(
    (item) => item.wagerType.code !== firstProbable?.wagerType.code
  );

  const buildFirstProbable = {
    title: `$${firstProbable?.minWagerAmount} ${firstProbable?.wagerType.name}`,
    description: `${getFormattedAmount(firstProbable?.amount, 0)}`,
    value: firstProbable?.wagerType.code || ""
  };

  const buildLeftProbables = leftProbables?.map((item) => ({
    title: `$${item.minWagerAmount} ${item.wagerType.name}`,
    description: `${getFormattedAmount(item?.amount, 0)}`,
    value: item.wagerType.code
  }));

  return buildFirstProbable
    ? [buildFirstProbable, ...(buildLeftProbables || [])]
    : [];
};

/**
 * Builds a SelectListObject array from the probables array
 * @param probables probables from the current race
 * @returns select list
 */
export const getWagersSelectList = (
  probables: Probable[] | undefined
): SelectListObject[] =>
  probables?.map((item) => ({
    label: `${formatCurrency(
      item.minWagerAmount,
      "USD",
      item.minWagerAmount < 1 ? 2 : 0 // Precision calc to match MEP
    )} ${item.wagerType.name}`,
    value: item.wagerType.code
  })) || [];

// Select onChange is passing a NativeSyntheticEvent<TextInputChangeEventData> but the value is actually a string
export const onWagerTypeSelectChange =
  (
    probables: Probable[],
    setSelectedWagerType: Dispatch<SetStateAction<WagerType>>,
    raceNumber: string,
    trackName: string
  ) =>
  (value: unknown) => {
    const condition = (item: Probable) => item.wagerType.code === value;
    const currentProbable = probables.find(condition) || probables[0];
    const currentProbableIndex = probables.findIndex(condition) || 0;
    const gtmLabel =
      getWagersSelectList(probables)[currentProbableIndex]?.label || "";
    gtmOnSelectProbablesOption({
      raceNumber: raceNumber || "",
      trackName: trackName || "",
      label: gtmLabel
    });
    setSelectedWagerType(currentProbable.wagerType);
  };

/**
 * Builds the columns array to pass into the table
 * @param probable probables dataset from current race
 * @returns array of columns to pass into table
 */
export const buildCols = (probable: Probable | undefined): TableColumns[] => {
  if (!probable) return [];

  // Gets an unique array of all the runner2 values
  const uniqueValues = new Set(
    probable.betCombos.map((combo) => combo.runner1 || "")
  );

  // Creates an array with a first value to specify we want a specific config for the first column
  // and then we push an object for each runner id
  const cols = [
    {
      dataKey: "saddleStatic",
      isFixed: true,
      width: 44
    },
    ...Array.from(uniqueValues).map((saddleNum) => ({
      dataKey: `saddle${saddleNum}`
    }))
  ];

  return cols;
};

/**
 * Searches for a probable with an EXACTA wager type or else returns the first in the list
 * @param probables List of probables
 * @returns probable
 */
export const getDefaultWagerType = (probables: Probable[]) =>
  probables.find((item) => item.wagerType.code === WagerTypeCodesEnum.EXACTA)
    ?.wagerType || probables[0].wagerType;
